import { useState, useMemo } from 'react'
import { Dialog } from 'radix-ui'
import { useAnnexableFields } from '../hooks'
import type { AnnexableFieldDto, AnnexableSectionDto } from '../types'

export type PickedField = {
  section: AnnexableSectionDto
  field: AnnexableFieldDto
}

export function AnnexableFieldsPicker({
  contractId,
  open,
  onClose,
  onPick,
  disabledFieldCodes = new Set(),
}: {
  contractId: number
  open: boolean
  onClose: () => void
  onPick: (picked: PickedField) => void
  disabledFieldCodes?: Set<string>
}) {
  const { data, isLoading, error } = useAnnexableFields(contractId, open)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!data) return []
    const needle = search.trim().toLowerCase()
    // FILE/GALLERY are managed in the dedicated Files tab and never persisted as
    // annex changes -- hide them from the picker.
    // Presentational types (SEPARATOR/SPACER/INSTRUCTION/HEADER) carry no data either.
    const hidden = new Set(['FILE', 'GALLERY', 'SEPARATOR', 'SPACER', 'INSTRUCTION', 'HEADER'])
    return data.sections
      .map(s => ({
        ...s,
        fields: s.fields.filter(f => {
          if (hidden.has(f.fieldType)) return false
          if (!needle) return true
          return (
            f.labelPl.toLowerCase().includes(needle) ||
            f.fieldCode.toLowerCase().includes(needle)
          )
        }),
      }))
      .filter(s => s.fields.length > 0)
  }, [data, search])

  return (
    <Dialog.Root open={open} onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[520px] max-h-[80vh] shadow-xl flex flex-col">
          <Dialog.Title className="text-base font-semibold">Dodaj pole do aneksu</Dialog.Title>
          <input
            type="text"
            placeholder="Szukaj pola..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="mt-3 px-3 py-2 border rounded text-sm"
            autoFocus
          />
          <div className="mt-3 flex-1 overflow-auto">
            {isLoading && <p className="text-sm text-slate-500">Ładowanie...</p>}
            {error && <p className="text-sm text-red-600">Błąd: {String(error)}</p>}
            {filtered.length === 0 && !isLoading && (
              <p className="text-sm text-slate-500">Brak pasujących pól.</p>
            )}
            {filtered.map(section => (
              <SectionGroup
                key={section.id}
                section={section}
                disabledFieldCodes={disabledFieldCodes}
                onPick={field => {
                  onPick({ section, field })
                  onClose()
                }}
              />
            ))}
          </div>
          <div className="flex justify-end mt-3">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded text-sm">
              Anuluj
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function SectionGroup({
  section,
  disabledFieldCodes,
  onPick,
}: {
  section: AnnexableSectionDto
  disabledFieldCodes: Set<string>
  onPick: (field: AnnexableFieldDto) => void
}) {
  return (
    <div className="mb-3">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{section.labelPl}</div>
      <ul className="flex flex-col">
        {section.fields.map(field => {
          const disabled = disabledFieldCodes.has(field.fieldCode)
          return (
            <li key={field.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onPick(field)}
                className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between"
              >
                <span>{field.labelPl}</span>
                <span className="text-xs text-slate-400">{field.fieldType}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
